// Waveform peak envelope: per-bucket min/max over a channel. The pad
// renders one vertical span per bucket (top = max, bottom = min), so
// this tells it exactly what to draw at every zoom level.
//
// Algorithm is deliberately trivial: linear scan, constant-time per
// frame. The win is moving it off the main thread for long buffers
// (a 2-minute stereo 48k take is ~10M frames; that's a frame budget
// hit on the main thread).
//
// Output shape keeps channels separate so the pad can stack L/R, but
// the pad is free to mix to mono for preview.

export type PeakEnvelope = {
  /** Number of display buckets. */
  buckets: number;
  /** One Float32Array per channel, length = buckets, interleaved [min, max]. */
  perChannel: Float32Array[];
};

export type ComputePeaksRequest = {
  id: number;
  channels: Float32Array[];
  buckets: number;
};

export type ComputePeaksResponse = {
  id: number;
  perChannel: Float32Array[];
  buckets: number;
};

// Pure function — the worker just invokes this. Keeping it here lets us
// unit-test it without a Worker context.
export function computePeaks(
  channels: Float32Array[],
  buckets: number,
): PeakEnvelope {
  const b = Math.max(1, Math.floor(buckets));
  const perChannel = channels.map((ch) => peaksForChannel(ch, b));
  return { buckets: b, perChannel };
}

function peaksForChannel(samples: Float32Array, buckets: number): Float32Array {
  const out = new Float32Array(buckets * 2);
  if (samples.length === 0) return out;
  // framesPerBucket may be fractional when buckets > samples. Track the
  // float cursor separately so we don't accumulate rounding error across
  // buckets at low zoom.
  const framesPerBucket = samples.length / buckets;
  let cursor = 0;
  for (let b = 0; b < buckets; b++) {
    const start = Math.floor(cursor);
    cursor += framesPerBucket;
    const end = Math.min(samples.length, Math.max(start + 1, Math.floor(cursor)));
    let min = Infinity;
    let max = -Infinity;
    for (let i = start; i < end; i++) {
      const v = samples[i]!;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (min === Infinity) {
      min = 0;
      max = 0;
    }
    out[b * 2] = min;
    out[b * 2 + 1] = max;
  }
  return out;
}
